import { useState } from 'react';
import { type CrmAgencyConnectionProviderResponse, type Provider } from '@urbanx/agx.crm.client';
import { AgxButton, AgxModal, AgxSpacer, AgxTextInput, Spinner } from '@urbanx/agx.ui-components2';
import { getEditableFields } from './helpers';

interface CrmConnectModalProps {
  providerDetails: CrmAgencyConnectionProviderResponse;
  busy: boolean;
  onConnect: (provider: Provider, fieldValues: Record<string, string>) => Promise<boolean>;
  onClose: () => void;
}

const CrmConnectModal = ({ providerDetails, busy, onConnect, onClose }: CrmConnectModalProps) => {
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  if (!providerDetails.provider) return null;

  const provider = providerDetails.provider;
  const providerName = providerDetails.displayName || provider;
  const editableFields = getEditableFields(providerDetails.fields);
  const fieldValues = Object.fromEntries(
    editableFields.map((field) => [field.name!, drafts[field.name!]?.trim() ?? ''])
  );
  const canConnect = editableFields.length > 0 && Object.values(fieldValues).every(Boolean);

  const connect = () => {
    void onConnect(provider, fieldValues).then((connected) => {
      if (connected) onClose();
    });
  };

  return (
    <AgxModal
      title={`Connect ${providerName} CRM`}
      type="dialog"
      variant="withDivider"
      actionButtonContainerStyle="withDivider"
      minWidth="500px"
      maxWidth="500px"
      isOpen
      setIsDialogOpen={(open) => {
        if (!open) onClose();
      }}
      content={
        <AgxSpacer
          direction="vertical"
          gap="spacing-md"
          fill
          padding={{ top: 'md', bottom: 'md', left: 'xl', right: 'xl' }}
        >
          {editableFields.map((field) => (
            <AgxTextInput
              id={`${provider}-${field.name}`}
              key={`${provider}-${field.name}`}
              type={field.sensitive ? 'password' : 'text'}
              label={field.label || 'ID'}
              required
              hideOptionalText
              defaultValue={drafts[field.name!] ?? ''}
              disabled={busy}
              onChange={(value) =>
                setDrafts((currentDrafts) => ({ ...currentDrafts, [field.name!]: value }))
              }
              stretch
            />
          ))}
        </AgxSpacer>
      }
      actionButtons={
        <AgxSpacer direction="horizontal" gap="spacing-md">
          <AgxButton
            id={`${provider}-cancel-connect`}
            text="Cancel"
            size="medium"
            variant="hollow"
            disabled={busy}
            onClick={onClose}
          />
          <AgxButton
            id={`${provider}-confirm-connect`}
            text={busy ? 'Connecting' : 'Connect'}
            size="medium"
            variant="primary"
            leftIcon={busy ? <Spinner className="crmCard__buttonSpinner" size={16} /> : undefined}
            disabled={busy || !canConnect}
            onClick={connect}
          />
        </AgxSpacer>
      }
    />
  );
};

export default CrmConnectModal;
